// src/components/dashboard/UpcomingJobs.js
import React from 'react';
import styled from 'styled-components';
import { 
  FaCalendarAlt, 
  FaMapMarkerAlt, 
  FaUser, 
  FaPhoneAlt,
  FaBriefcase,
  FaExternalLinkAlt
} from 'react-icons/fa';
import DashboardCard from './DashboardCard';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../utils/utils';

const JobsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing(2)};
`;

const JobItem = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(2)};
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border-left: 4px solid ${props => 
    isToday(props.startDate) || isTomorrow(props.startDate)
      ? props.theme.colors.primary
      : props.theme.colors.inputGray
  };
  
  &:hover {
    transform: translateX(4px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const JobHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing(1)};
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: ${props => props.theme.spacing(1)};
  }
`;

const JobTitle = styled.div`
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  font-size: ${props => props.theme.typography.fontSize.medium};
  color: ${props => props.theme.colors.textPrimary};
`;

const JobSchedule = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => 
    isToday(props.startDate)
      ? props.theme.colors.primary
      : isTomorrow(props.startDate)
        ? props.theme.colors.success
        : props.theme.colors.textSecondary
  };
  font-weight: ${props => 
    isToday(props.startDate) || isTomorrow(props.startDate)
      ? props.theme.typography.fontWeight.medium
      : props.theme.typography.fontWeight.regular
  };
`;

const JobDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${props => props.theme.spacing(1)};
  margin: ${props => props.theme.spacing(1)} 0;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const JobDetail = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.theme.colors.textSecondary};
  
  svg {
    color: ${props => props.theme.colors.primary};
    flex-shrink: 0;
  }
`;

const JobServices = styled.div`
  margin-top: ${props => props.theme.spacing(1)};
  padding-top: ${props => props.theme.spacing(1)};
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.theme.colors.textSecondary};
  
  .services-title {
    font-weight: ${props => props.theme.typography.fontWeight.medium};
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing(0.5)};
    margin-bottom: ${props => props.theme.spacing(0.5)};
  }
  
  .service-item {
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing(1)};
    margin-top: ${props => props.theme.spacing(0.5)};
  }
`;

const JobPrice = styled.div`
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.success};
  font-size: ${props => props.theme.typography.fontSize.medium};
  margin-top: ${props => props.theme.spacing(1)};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ViewButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(0.5)};
  background-color: ${props => props.theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(1)} ${props => props.theme.spacing(2)};
  cursor: pointer;
  font-size: ${props => props.theme.typography.fontSize.small};
  
  &:hover {
    opacity: 0.9;
  }
`;

const ScheduleIndicator = styled.div`
  display: inline-block;
  background-color: ${props => 
    isToday(props.date)
      ? props.theme.colors.primary + '33'
      : isTomorrow(props.date)
        ? props.theme.colors.success + '33'
        : 'transparent'
  };
  color: ${props => 
    isToday(props.date)
      ? props.theme.colors.primary
      : isTomorrow(props.date)
        ? props.theme.colors.success
        : props.theme.colors.textSecondary
  };
  border-radius: 12px;
  padding: ${props => 
    isToday(props.date) || isTomorrow(props.date)
      ? `${props.theme.spacing(0.5)} ${props.theme.spacing(1)}`
      : '0'
  };
  font-weight: ${props => 
    isToday(props.date) || isTomorrow(props.date)
      ? props.theme.typography.fontWeight.medium
      : props.theme.typography.fontWeight.regular
  };
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing(4)};
  color: ${props => props.theme.colors.textSecondary};
`;

// Helper to check if a date is today
const isToday = (dateString) => {
  if (!dateString) return false;
  const date = new Date(dateString);
  const today = new Date();
  return date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();
};

// Helper to check if a date is tomorrow
const isTomorrow = (dateString) => {
  if (!dateString) return false;
  const date = new Date(dateString);
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return date.getDate() === tomorrow.getDate() &&
    date.getMonth() === tomorrow.getMonth() &&
    date.getFullYear() === tomorrow.getFullYear();
};

// Helper to format date range
const formatDateRange = (startDate, endDate) => {
  if (!startDate) return '';
  
  const start = new Date(startDate);
  const startFormatted = start.toLocaleDateString();
  
  // If no end date or end date is the same as start date
  if (!endDate || new Date(endDate).toDateString() === start.toDateString()) {
    return startFormatted;
  }
  
  // If different dates
  const end = new Date(endDate);
  const endFormatted = end.toLocaleDateString();
  return `${startFormatted} - ${endFormatted}`;
};

// Helper to format phone numbers
const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  const cleaned = ('' + phoneNumber).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phoneNumber;
};

const UpcomingJobs = ({ data, loading, error }) => {
  const navigate = useNavigate();

  const handleJobClick = (jobId) => {
    navigate(`/jobs/${jobId}`);
  };

  return (
    <DashboardCard
      title="Upcoming Jobs"
      icon={<FaCalendarAlt />}
      viewAllLink="/jobs"
      loading={loading}
      error={error}
    >
      {data && data.length > 0 ? (
        <JobsWrapper>
          {data.map((job) => (
            <JobItem 
              key={job.job_id} 
              startDate={job.scheduled_start_date}
              onClick={() => handleJobClick(job.job_id)}
            >
              <JobHeader>
                <JobTitle>{job.customer_name}</JobTitle>
                <JobSchedule startDate={job.scheduled_start_date}>
                  <FaCalendarAlt />
                  <ScheduleIndicator date={job.scheduled_start_date}>
                    {isToday(job.scheduled_start_date)
                      ? 'Today'
                      : isTomorrow(job.scheduled_start_date)
                        ? 'Tomorrow'
                        : formatDateRange(job.scheduled_start_date, job.scheduled_end_date)}
                  </ScheduleIndicator>
                </JobSchedule>
              </JobHeader>
              
              <JobDetails>
                <JobDetail>
                  <FaMapMarkerAlt />
                  {job.job_address}, {job.job_city}
                </JobDetail>
                
                <JobDetail>
                  <FaPhoneAlt />
                  {formatPhoneNumber(job.customer_phone)}
                </JobDetail>
                
                <JobDetail>
                  <FaUser />
                  {job.customer_name}
                </JobDetail>
              </JobDetails>
              
              {job.services && job.services.length > 0 && (
                <JobServices>
                  <div className="services-title">
                    <FaBriefcase />
                    Services:
                  </div>
                  
                  {job.services.map((service, index) => (
                    <div key={index} className="service-item">
                      - {service.service_name}
                    </div>
                  ))}
                </JobServices>
              )}
              
              <JobPrice>
                {formatCurrency(job.total_price)}
                
                <ViewButton onClick={(e) => {
                  e.stopPropagation();
                  handleJobClick(job.job_id);
                }}>
                  View Job
                  <FaExternalLinkAlt />
                </ViewButton>
              </JobPrice>
            </JobItem>
          ))}
        </JobsWrapper>
      ) : (
        <EmptyState>No upcoming jobs found</EmptyState>
      )}
    </DashboardCard>
  );
};

export default UpcomingJobs;